import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/Dialog';
import StatGrid from './StatGrid';
import OnboardingCard from './OnboardingCard';
import {
  ApiError,
  claimNextSubmission,
  getModeratorStats,
  getQueueConfig,
  getStats,
  inviteModerator,
  putQueueConfig,
  suspendModerator,
  unsuspendModerator,
} from '../lib/api';
import type {
  ClaimNextNotBalancedError,
  ModeratorStatsResponse,
  QueueConfig,
  StatsResponse,
} from '../types/moderation';
import { useCurrentUser } from '../lib/auth';
import AlertsBanner from './AlertsBanner';
import ErrorState from './ErrorState';

interface DashboardProps {
  onNavigate: (hash: string) => void;
}

function formatSeconds(s: number | null): string {
  if (s === null || !Number.isFinite(s)) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

export default function Dashboard({ onNavigate }: DashboardProps) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [claimNextBusy, setClaimNextBusy] = useState(false);
  const [claimNextError, setClaimNextError] = useState<string | null>(null);
  /**
   * Holds the 409 NOT_BALANCED payload so the UI can show the
   * underloaded moderator's email + an admin-only override button.
   */
  const [notBalanced, setNotBalanced] = useState<ClaimNextNotBalancedError | null>(
    null,
  );
  const [moderatorStats, setModeratorStats] = useState<ModeratorStatsResponse | null>(null);
  const [moderatorStatsError, setModeratorStatsError] = useState<string | null>(null);
  const [queueConfig, setQueueConfig] = useState<QueueConfig | null>(null);
  const [queueConfigBusy, setQueueConfigBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'volunteer' | 'lead_moderator' | 'admin'>(
    'volunteer',
  );
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  /**
   * F-002: in-app dialog state for the "suspend moderator" confirm.
   * Replaces the previous `window.confirm()` call so the action is
   * styleable, keyboard-trapped, and works inside iframes/extensions.
   */
  const [suspendTarget, setSuspendTarget] = useState<string | null>(null);
  const [suspendBusy, setSuspendBusy] = useState(false);

  const currentUser = useCurrentUser();
  const role = currentUser?.role ?? 'volunteer';
  const isLeadOrAdmin = role === 'lead_moderator' || role === 'admin';
  const isAdmin = role === 'admin';

  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getStats()
      .then((data) => {
        if (!cancelled) {
          setStats(data);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load stats');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // Fetch moderator activity + queue config when the role unlocks them.
  useEffect(() => {
    if (!isLeadOrAdmin) return;
    let cancelled = false;
    getModeratorStats()
      .then((data) => {
        if (!cancelled) setModeratorStats(data);
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setModeratorStatsError(err.message || 'Failed to load moderator stats');
        }
      });
    getQueueConfig()
      .then((data) => {
        if (!cancelled) setQueueConfig(data);
      })
      .catch(() => {
        // Non-fatal — queue config has a default.
      });
    return () => {
      cancelled = true;
    };
  }, [isLeadOrAdmin]);

  const handleClaimNext = async (force = false) => {
    setClaimNextBusy(true);
    setClaimNextError(null);
    if (!force) {
      // Reset any previous balance banner on a fresh non-forced attempt.
      setNotBalanced(null);
    }
    try {
      const res = await claimNextSubmission(force);
      setNotBalanced(null);
      onNavigate(`#/submissions/${encodeURIComponent(res.record.submissionId)}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setClaimNextError('No claimable submissions right now. Nice work!');
      } else if (err instanceof ApiError && err.status === 503) {
        setClaimNextError(
          'Auto-assignment is currently turned off. Pick a submission from the queue, or ask an admin to enable it.',
        );
      } else if (
        err instanceof ApiError &&
        err.status === 409 &&
        typeof err.body === 'object' &&
        err.body !== null &&
        (err.body as { code?: string }).code === 'NOT_BALANCED'
      ) {
        setNotBalanced(err.body as ClaimNextNotBalancedError);
      } else {
        setClaimNextError((err as Error).message || 'Could not claim a submission');
      }
    } finally {
      setClaimNextBusy(false);
    }
  };

  const toggleAutoAssign = async () => {
    if (!queueConfig) return;
    const next: QueueConfig = {
      ...queueConfig,
      autoAssign: !queueConfig.autoAssign,
    };
    setQueueConfigBusy(true);
    try {
      const saved = await putQueueConfig(next);
      setQueueConfig(saved);
    } catch {
      // ignore — keep previous local state
    } finally {
      setQueueConfigBusy(false);
    }
  };

  const submitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteBusy(true);
    setInviteMessage(null);
    setInviteError(null);
    try {
      const res = await inviteModerator(inviteEmail.trim(), inviteRole);
      setInviteMessage(
        `Invite sent to ${res.email}. Link expires ${new Date(res.expiresAt).toLocaleString()}.`,
      );
      setInviteEmail('');
    } catch (err) {
      setInviteError((err as Error).message || 'Invite failed');
    } finally {
      setInviteBusy(false);
    }
  };

  const handleSuspend = async (email: string, currentlySuspended: boolean) => {
    try {
      if (currentlySuspended) {
        await unsuspendModerator(email);
        const data = await getModeratorStats();
        setModeratorStats(data);
      } else {
        // F-002: open the in-app confirm dialog; the actual API call
        // happens in confirmSuspend() when the user confirms.
        setSuspendTarget(email);
      }
    } catch (err) {
      setModeratorStatsError((err as Error).message || 'Could not update suspension');
    }
  };

  const confirmSuspend = async () => {
    if (!suspendTarget) return;
    setSuspendBusy(true);
    try {
      await suspendModerator(suspendTarget);
      const data = await getModeratorStats();
      setModeratorStats(data);
      setSuspendTarget(null);
    } catch (err) {
      setModeratorStatsError((err as Error).message || 'Could not update suspension');
    } finally {
      setSuspendBusy(false);
    }
  };

  if (loading) {
    // F-204: delegate to StatGrid (also gives us the F-213 stable
    // element type during loading).
    return (
      <div className="dashboard">
        <h1>Dashboard</h1>
        <StatGrid stats={null} loading onNavigate={onNavigate} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <h1>Dashboard</h1>
        <ErrorState
          message="We could not load the dashboard statistics."
          detail={error}
          onRetry={reload}
          testId="dashboard-error"
        />
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      <AlertsBanner stats={stats} onNavigate={onNavigate} />
      {/* F-301: first-run welcome card (auto-dismissed forever after
          the user closes it once). */}
      {currentUser?.sub && (
        <OnboardingCard userKey={currentUser.sub} onNavigate={onNavigate} />
      )}
      <StatGrid stats={stats} onNavigate={onNavigate} />

      <section className="dashboard__claim-next" aria-label="Pick a submission">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => handleClaimNext(false)}
          disabled={claimNextBusy}
          data-testid="claim-next"
        >
          {claimNextBusy ? 'Claiming…' : 'Claim next'}
        </button>
        {claimNextError && (
          <div className="field-error" role="alert" data-testid="claim-next-error">
            {claimNextError}
          </div>
        )}
        {notBalanced && (
          <div
            className="field-info dashboard__balance-banner"
            role="status"
            data-testid="claim-next-not-balanced"
          >
            <p>
              Hold on. {notBalanced.mostUnderloaded} has fewer open claims
              ({notBalanced.lowestClaimCount}) than you ({notBalanced.yourClaimCount}).
              Give them a chance to pick this one up, or pick a submission from the
              queue yourself.
            </p>
            {isAdmin && (
              <button
                type="button"
                className="btn btn-link"
                onClick={() => handleClaimNext(true)}
                disabled={claimNextBusy}
                data-testid="claim-next-force"
              >
                Claim anyway
              </button>
            )}
          </div>
        )}
      </section>

      {isLeadOrAdmin && (
        <section className="dashboard__moderator-stats" aria-label="Moderator activity">
          <h2>Moderator activity (last 7 days)</h2>
          {moderatorStatsError && (
            <div className="error-state" role="alert" data-testid="moderator-stats-error">
              {moderatorStatsError}
            </div>
          )}
          {moderatorStats && moderatorStats.rows.length === 0 && (
            <p data-testid="moderator-stats-empty">No moderator activity in the last 7 days.</p>
          )}
          {moderatorStats && moderatorStats.rows.length > 0 && (
            <table className="data-table" data-testid="moderator-stats-table">
              <thead>
                <tr>
                  <th scope="col">Moderator</th>
                  <th scope="col">Reviewed</th>
                  <th scope="col">Approved</th>
                  <th scope="col">Rejected</th>
                  <th scope="col">Avg time</th>
                  {isAdmin && <th scope="col">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {moderatorStats.rows.map((row) => (
                  <tr key={row.userEmail}>
                    <td>{row.userEmail}</td>
                    <td>{row.reviewed}</td>
                    <td>{row.approved}</td>
                    <td>{row.rejected}</td>
                    <td>{formatSeconds(row.avgTimeSeconds)}</td>
                    {isAdmin && (
                      <td>
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={() => handleSuspend(row.userEmail, false)}
                          data-testid={`suspend-${row.userEmail}`}
                        >
                          Suspend
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {isAdmin && (
        <section className="dashboard__admin" aria-label="Admin controls">
          <h2>Admin controls</h2>
          {queueConfig && (
            <div className="dashboard__queue-config">
              <label className="form-group">
                <input
                  type="checkbox"
                  checked={queueConfig.autoAssign}
                  onChange={toggleAutoAssign}
                  disabled={queueConfigBusy}
                  data-testid="queue-auto-assign"
                />
                <span>
                  Auto-assign submissions on "Claim next" (round-robin)
                </span>
              </label>
            </div>
          )}
          <form onSubmit={submitInvite} className="dashboard__invite-form" data-testid="invite-form">
            <h3>Invite a moderator</h3>
            <label className="form-group">
              <span>Email</span>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                data-testid="invite-email"
              />
            </label>
            <label className="form-group">
              <span>Role</span>
              <select
                value={inviteRole}
                onChange={(e) =>
                  setInviteRole(
                    e.target.value as 'volunteer' | 'lead_moderator' | 'admin',
                  )
                }
                data-testid="invite-role"
              >
                <option value="volunteer">Volunteer</option>
                <option value="lead_moderator">Lead moderator</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={inviteBusy || !inviteEmail.trim()}
              data-testid="invite-submit"
            >
              {inviteBusy ? 'Sending…' : 'Send invite'}
            </button>
            {inviteMessage && (
              <div className="field-info" role="status" data-testid="invite-success">
                {inviteMessage}
              </div>
            )}
            {inviteError && (
              <div className="field-error" role="alert" data-testid="invite-error">
                {inviteError}
              </div>
            )}
          </form>
        </section>
      )}

      {/* F-002: in-app confirm for suspending a moderator */}
      <Dialog
        open={suspendTarget !== null}
        onOpenChange={(o) => {
          if (!o && !suspendBusy) setSuspendTarget(null);
        }}
      >
        {suspendTarget && (
          <DialogContent>
            <DialogTitle>Suspend moderator?</DialogTitle>
            <DialogDescription>
              Suspend <strong>{suspendTarget}</strong>? They will lose all moderation access
              and any active claims will be released.
            </DialogDescription>
            <div className="dlg-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setSuspendTarget(null)}
                disabled={suspendBusy}
                data-testid="suspend-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmSuspend}
                disabled={suspendBusy}
                data-testid="suspend-confirm"
              >
                {suspendBusy ? 'Suspending…' : 'Suspend'}
              </button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
